// #32 — plays the dock's wall item on this screen only (not synced), in the player module's PlayableMedia: an embed
// (YouTube, SoundCloud, …) shows as the card's video area (visible and interactive, at least 200×200: YouTube API
// terms); audio has no surface (the card draws art, progress and controls). This screen drives it: play / pause from
// the card, the hold, YouTube's own controls.
import { useEffect, useRef } from "react";
import type { WallItem } from "../../api/content";
import { PlayableMedia, IDLE_HANDLE, resolveItem, type MediaEvent, type MediaHandle } from "../../player";
import { isLinkItem } from "../../store/wallPlaybackStore";

export interface WallDockPlayerProps {
  item: WallItem;
  paused: boolean;
  onEnded(): void;
  onError(): void;
  onProgress(positionMs: number, durationMs: number | null): void;
  /** The dock's mini player: the video area is a 200×200 square beside the titles (same player, never remounted). */
  mini?: boolean;
  /**
   * A link item's video box went partly out of view (or back): the dock scrolled it, or the screen's edge cut
   * it. YouTube's player never plays while less than all of it shows, so the card holds it meanwhile.
   */
  onClipped?(clipped: boolean): void;
  /**
   * Wall playback (owner 2026-10-21): a YouTube item shows YouTube's own controls (the card hides the ones they
   * duplicate). true once the hosted player takes them; false for SoundCloud, audio, an older hosted player, or unmount.
   */
  onOwnControls?(own: boolean): void;
  /** The user paused (true) or played (false) inside YouTube's own controls; never our own play / pause. */
  onUserPaused?(paused: boolean): void;
  /** The card's wrapper is fullscreen: the video area fills it (the wrapper, not this per-item player, is the element). */
  fullscreen?: boolean;
  /** The card holds the item (`paused` includes it): YouTube's own play must not start it meanwhile. */
  held?: boolean;
  /**
   * Full screen: the player area is exactly the 16:9 box (anything under it is clipped), so the link player draws no
   * Tap to listen of its own; it hands the tap here and the card shows it in the controls' band. null = none needed.
   */
  onTapToListen?(tap: (() => void) | null): void;
}

/** A player event this soon after our own play / pause is that command settling, not the user. */
const OWN_COMMAND_MS = 500;

function useLatest<T>(v: T) {
  const r = useRef(v);
  r.current = v;
  return r;
}

export default function WallDockPlayer(props: WallDockPlayerProps) {
  // Keyed by item: a new item is a fresh player (the old one is destroyed on unmount).
  return <ItemPlayer key={props.item.id} {...props} />;
}

function ItemPlayer({ item, paused, held = false, onEnded, onError, onProgress, mini, fullscreen = false, onClipped, onOwnControls, onUserPaused, onTapToListen }: WallDockPlayerProps) {
  const resolved = resolveItem(item);
  const { kind } = resolved;
  const link = isLinkItem(item);
  const box = useRef<HTMLDivElement>(null);
  const media = useRef<MediaHandle>(IDLE_HANDLE);
  const heldRef = useLatest(held);
  const onClippedRef = useLatest(onClipped);
  const duration = useRef<number | null>(item.durationMs);
  const cb = useLatest({ onEnded, onError, onProgress, onOwnControls, onUserPaused });
  const pausedRef = useLatest(paused);
  // YouTube's own controls are on; what we last asked the player for, and when.
  const own = useRef(false);
  const cmd = useRef<{ play: boolean; at: number }>({ play: !paused, at: Date.now() });
  // The player's states since our last command, and the one re-read when its echo window ends.
  const sinceCmd = useRef<string[]>([]);
  const recheck = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Our own play / pause: its echoes are not the user's; once they settle, re-read what the player did meanwhile. */
  const command = (play: boolean) => {
    cmd.current = { play, at: Date.now() };
    sinceCmd.current = [];
    if (recheck.current) clearTimeout(recheck.current);
    recheck.current = !own.current
      ? null
      : setTimeout(() => {
          recheck.current = null;
          // Played, then paused inside the window: the user's pause, which the echo rule dropped. (A lone late
          // "paused" is an older command settling and stays ignored.)
          const s = sinceCmd.current;
          if (cmd.current.play && s[s.length - 1] === "paused" && s.includes("playing")) userState(false);
        }, OWN_COMMAND_MS);
  };
  const userState = (isPlay: boolean) => {
    cmd.current = { play: isPlay, at: Date.now() };
    sinceCmd.current = [];
    cb.current.onUserPaused?.(!isPlay);
  };

  const onEvent = (e: MediaEvent) => {
    if (e.type === "controls") {
      own.current = e.own;
      command(!pausedRef.current);
      if (e.own) cb.current.onOwnControls?.(true);
    } else if (e.type === "loaded") {
      if (e.durationMs != null) duration.current = e.durationMs;
    } else if (e.type === "state") {
      // Only YouTube's own controls can pause / play it besides us: a state against our last command,
      // well after it, is the user's.
      if (own.current) sinceCmd.current.push(e.state);
      if (own.current && (e.state === "paused" || e.state === "playing")) {
        const isPlay = e.state === "playing";
        if (isPlay && heldRef.current) {
          // Held (clipped / covered) yet YouTube's play was pressed: the hold wins, it pauses again.
          command(false);
          media.current.pause();
        } else if (isPlay !== cmd.current.play && Date.now() - cmd.current.at >= OWN_COMMAND_MS) {
          userState(isPlay);
        }
      }
      cb.current.onProgress(e.positionMs, duration.current);
    } else if (e.type === "position") {
      cb.current.onProgress(e.positionMs, e.durationMs !== undefined ? e.durationMs : duration.current);
    } else if (e.type === "ended") {
      // A live stream never ends by itself: a stream the server closed is an error, never an advance.
      if (kind.caps.endsByItself) cb.current.onEnded();
      else cb.current.onError();
    } else if (e.type === "error") {
      cb.current.onError();
    }
  };

  useEffect(
    () => () => {
      if (own.current) cb.current.onOwnControls?.(false);
      own.current = false;
      if (recheck.current) clearTimeout(recheck.current);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Fully visible = the whole 200px box intersects the viewport after every ancestor's clip (the dock's scroll).
  useEffect(() => {
    const el = box.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[entries.length - 1];
        if (e) onClippedRef.current?.(!(e.isIntersecting && e.intersectionRatio >= 0.99));
      },
      { threshold: [0, 0.99, 1] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      onClippedRef.current?.(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstPaused = useRef(true);
  useEffect(() => {
    // The initial state went with the load; later changes are the card's play / pause.
    if (firstPaused.current) {
      firstPaused.current = false;
      return;
    }
    // Already in that state (the user did it in YouTube's controls, the card followed): nothing to send.
    if (cmd.current.play === !paused) return;
    command(!paused);
    if (paused) media.current.pause();
    else media.current.play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  // Full screen: the card's band draws Tap to listen (nothing over the player, nothing clipped under it).
  const inBand = fullscreen && onTapToListen != null;
  return (
    <div data-testid="wall-dock-player" data-kind={link ? "link" : "audio"} className={link ? (fullscreen ? "w-full aspect-video" : mini ? "shrink-0" : "shrink-0 px-2 pb-2") : undefined}>
      <PlayableMedia
        item={item}
        resolved={resolved}
        handleRef={media}
        onEvent={onEvent}
        autoPlay={() => !pausedRef.current}
        shouldPlay={!paused}
        providerControls
        // 200px tall at any dock width (the small card is 240 wide → 224×200), a 200×200 square in the mini player:
        // the API's minimum player size.
        frame={{
          ref: box,
          testId: "wall-dock-video-box",
          className: `relative shrink-0 overflow-hidden bg-black ${fullscreen ? "w-full h-full" : mini ? "size-[200px] rounded-xl" : "w-full min-w-[200px] h-[200px] rounded-xl"}`,
        }}
        hostTestId={link ? "wall-dock-video" : "wall-dock-audio"}
        // Already inside padded content when it is a link (YouTube's Tap to listen sits under its video).
        tapToListenClassName={link ? "pt-2" : "px-2 pt-2"}
        tapToListenOut={inBand ? onTapToListen : undefined}
      />
    </div>
  );
}
